import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, LogOut, RefreshCw } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { useAuth } from "../hooks/useAuth";
import { LoginGate } from "../components/LoginGate";
import { DisplayKeys } from "../components/DisplayKeys";
import { HygieneAdmin } from "../components/HygieneAdmin";
import { AiDecisions } from "../components/AiDecisions";
import { api, errorText } from "../lib/api";
import { PRODUCT_LABEL, PRODUCT_ORDER, PRODUCT_SLUG, countryName, fmt, fmtDate } from "../lib/constants";

const when = (iso) => (iso ? new Date(iso).toLocaleString("en-GB", { dateStyle: "medium", timeStyle: "medium" }) : "—");

const Card = ({ label, value, testId, tone = "" }) => (
  <div className="panel p-4">
    <div className="eyebrow">{label}</div>
    <div data-testid={testId} className={`num font-bold text-lg mt-1 break-words ${tone}`}>{value}</div>
  </div>
);

const DealList = ({ items, testId, render, empty = "Nothing here – all clean." }) => (
  <div data-testid={testId} className="panel divide-y divide-panel-border max-h-[60vh] overflow-y-auto thin-scroll">
    {!items?.length && <div className="p-4 text-sm text-slate-500">{empty}</div>}
    {items?.map((it, i) => (
      <div key={it.id || it.name || i} className="px-4 py-2 text-sm flex items-center gap-3">
        {render(it)}
      </div>
    ))}
  </div>
);

const Id = ({ id }) => <span className="num text-xs text-slate-500 w-28 shrink-0">{id}</span>;
const Country = ({ code }) => <span className="num text-xs text-slate-500">{!code || code === "ZZ" ? "—" : code}</span>;

// Debug view after the company_type change: how the fetched companies are typed and which venues lack a Merchant
const CompanyStats = ({ companies }) => {
  if (!companies) return <div className="panel p-4 text-sm text-slate-500">No snapshot yet</div>;
  const other = Object.entries(companies.by_type || {}).filter(([k]) => !["venue", "merchant", "<missing>"].includes(k));
  return (
    <div className="space-y-3" data-testid="company-stats">
      <div className="text-xs text-slate-500">
        Companies linked to Won / Legacy deals (plus the ones reached through Merchant ↔ Venue links), typed only by the HubSpot property <b>Company type</b>. Companies without a type (or with an unexpected value) are <b>unclassified</b> and never counted; the association label is not used for the type, only for the Merchant → Venue link.
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
        {[
          ["Companies fetched", companies.fetched, "company-stats-fetched", ""],
          ["Venue", companies.venues, "company-stats-venues", "text-live"],
          ["Merchant", companies.merchants, "company-stats-merchants", ""],
          ["Missing Company type", companies.missing_type, "company-stats-missing", companies.missing_type ? "text-brand" : ""],
          ["Unexpected value", companies.unexpected_type, "company-stats-unexpected", companies.unexpected_type ? "text-brand" : ""],
        ].map(([label, value, testId, tone]) => (
          <div key={label} className="panel p-3"><div className="eyebrow">{label}</div><div data-testid={testId} className={`num font-bold text-lg mt-1 ${tone}`}>{fmt(value || 0)}</div></div>
        ))}
      </div>
      {other.length > 0 && (
        <div className="text-xs text-slate-600" data-testid="company-stats-other-values">Unexpected Company type values: {other.map(([k, n]) => `“${k}” × ${n}`).join(", ")}</div>
      )}
      <div>
        <div className="eyebrow mb-1">Venues without a Merchant · {fmt(companies.venues_without_merchant?.length || 0)} <span className="text-slate-400 normal-case tracking-normal font-normal">(counted anyway – associate the Merchant company or set the Parent Company)</span></div>
        <DealList items={companies.venues_without_merchant} testId="company-stats-no-merchant" empty="Every venue has a Merchant." render={(it) => (<><Id id={it.id} /><span className="flex-1 truncate">{it.name}</span>{it.counted ? <span className="num text-[10px] font-bold text-live bg-live/10 rounded-full px-2 py-0.5">on board</span> : <span className="text-[10px] uppercase tracking-wider text-slate-400">no won / legacy deal</span>}<Country code={it.country} /></>)} />
      </div>
    </div>
  );
};

// HubSpot line-item names grouped by the product category they are counted under
const LineItemMapping = ({ items }) => {
  const [product, setProduct] = useState("Payments");
  const rows = (items || []).filter((it) => it.product === product);
  return (
    <div data-testid="line-item-mapping">
      <div className="flex flex-wrap gap-2 mb-3">
        {PRODUCT_ORDER.map((p) => {
          const n = (items || []).filter((it) => it.product === p).length;
          return (
            <button
              key={p}
              type="button"
              data-testid={`mapping-filter-${PRODUCT_SLUG(p)}`}
              onClick={() => setProduct(p)}
              className={`rounded-full border px-3 py-1 text-xs font-semibold transition-colors ${product === p ? "border-brand bg-brand/5 text-brand" : "border-panel-border bg-panel text-slate-600 hover:border-slate-400"}`}
            >
              {PRODUCT_LABEL[p]} <span className="num text-slate-400">{n}</span>
            </button>
          );
        })}
      </div>
      <DealList
        items={rows}
        testId={`line-item-mapping-${PRODUCT_SLUG(product)}`}
        empty="No line items in this category."
        render={(it) => (<><span className="flex-1 truncate">{it.name || "(unnamed line item)"}</span><span className="rounded-full bg-slate-100 text-slate-600 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide" data-testid={`mapping-kind-${it.kind || "none"}`}>{it.kind || "—"}</span><span className="num text-xs text-slate-500">×{it.count}</span></>)}
      />
    </div>
  );
};

export default function AdminPage() {
  const [roles, setRoles, , logout] = useAuth();
  const [status, setStatus] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await api.get("/admin/status");
      setStatus(r.data);
      setError("");
    } catch (e) {
      if (e.response?.status === 401) setRoles((r) => ({ ...(r || {}), admin: false }));
      else setError(errorText(e));
    }
  }, [setRoles]);

  useEffect(() => {
    if (!roles?.admin) return undefined;
    load();
    const id = setInterval(load, 5000);
    return () => clearInterval(id);
  }, [roles, load]);
  // Admin always stays light – the board's dark theme class never leaks here
  useEffect(() => {
    document.documentElement.classList.remove("dark");
  }, []);

  const refreshNow = async () => {
    setBusy(true);
    try {
      await api.post("/admin/refresh");
      setTimeout(load, 800);
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(false);
    }
  };

  if (roles === null) return <div className="min-h-screen bg-page" />;
  if (!roles.admin) return <LoginGate onSuccess={setRoles} />;

  const refresh = status?.refresh;
  const dq = status?.data_quality;
  const running = refresh?.running;
  const all = status?.views?.all;
  const since = status?.views?.since;
  const countFrom = fmtDate(status?.count_from);
  const trackingFrom = fmtDate(status?.tracking_from) || countFrom;

  return (
    <div className="min-h-screen bg-page text-ink p-4 sm:p-8" data-testid="admin-page">
      <div className="max-w-6xl mx-auto space-y-6">
        <header className="flex flex-wrap items-center gap-4">
          <Link to="/" data-testid="admin-back-link" className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-ink transition-colors">
            <ArrowLeft className="w-4 h-4" /> Wallboard
          </Link>
          <h1 className="flex items-center gap-3 font-bold text-2xl sm:text-3xl">
            <span className="inline-flex items-center bg-coal rounded-lg px-2.5 py-1.5"><img src="/fora-logo.png" alt="FORA" className="h-5 sm:h-6 w-auto" draggable={false} /></span>
            <span className="text-slate-500 font-semibold">Admin</span>
          </h1>
          <div className="flex-1" />
          <button
            type="button"
            data-testid="admin-refresh-button"
            onClick={refreshNow}
            disabled={busy || running}
            className="inline-flex items-center gap-2 rounded-full bg-brand hover:bg-[#c42320] disabled:opacity-50 px-5 py-2.5 font-semibold text-white shadow-sm transition-colors"
          >
            <RefreshCw className={`w-4 h-4 ${running ? "animate-spin" : ""}`} />
            {running ? "Refreshing…" : "Refresh now"}
          </button>
          <button type="button" data-testid="admin-logout-button" onClick={logout} className="inline-flex items-center gap-2 rounded-full border border-panel-border px-4 py-2.5 text-sm text-slate-600 hover:text-ink hover:border-slate-400 transition-colors">
            <LogOut className="w-4 h-4" /> Log out
          </button>
        </header>

        {error && <div className="panel p-4 text-brand text-sm" data-testid="admin-error">{error}</div>}

        <section className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <Card label="Last successful refresh" value={when(refresh?.last_success_at)} testId="admin-last-refresh-time" />
          <Card label="Last attempt" value={`${when(refresh?.last_attempt_at)}${refresh?.last_trigger ? ` · ${refresh.last_trigger}` : ""}`} testId="admin-last-attempt-time" />
          <Card
            label="Last result"
            value={running ? "running…" : refresh?.last_result === "ok" ? `OK · ${refresh?.duration_s ?? "?"}s` : refresh?.last_result === "error" ? "ERROR" : "—"}
            testId="admin-last-result"
            tone={refresh?.last_result === "error" ? "text-brand" : refresh?.last_result === "ok" ? "text-live" : ""}
          />
          <Card label="Refresh interval" value={`${Math.round((refresh?.interval_s || 0) / 60)} min · poll 30 s`} testId="admin-interval" />
        </section>
        {status?.targets && (
          <section className="panel p-4 flex flex-wrap gap-6 text-sm" data-testid="admin-targets">
            <div><span className="eyebrow block">Target month</span><span className="num font-bold">{status.targets.month}</span></div>
            <div><span className="eyebrow block">New venues</span><span className="num font-bold">{fmt(all?.new_clients?.this_month)} / {status.targets.monthly_clients ? fmt(status.targets.monthly_clients) : "—"}</span></div>
            <div><span className="eyebrow block">Payment venues this month</span><span className="num font-bold">{fmt(all?.new_clients?.this_month_payments)} / {status.targets.monthly_payments ? fmt(status.targets.monthly_payments) : "—"}{all?.new_clients?.this_month_payments_existing ? <span className="text-slate-400 font-normal text-xs"> · incl. {fmt(all.new_clients.this_month_payments_existing)} existing</span> : null}</span></div>
            <div><span className="eyebrow block">Daily target</span><span className="num font-bold">{fmt(all?.new_clients?.today)} / {status.targets.daily_clients ? fmt(status.targets.daily_clients) : "—"}</span></div>
            <div className="text-slate-500 self-end text-xs" data-testid="admin-counting-rules">
              Counting unit = venue = HubSpot company with <b>Company type = Venue</b> (the property, never the association label) linked to a WON or "Legacy – Active" deal. A deal linked only to a Merchant counts for that Merchant's venues (company-to-company link); a deal on no Venue is not counted. Products = line items of WON deals, de-duplicated per venue (one venue + one product = 1, whatever the quantity); every line item maps to a product category – "Others" only catches names no rule recognises. Country = venue country first. Daily target = monthly target ÷ days in month (backend/config.py MONTHLY_TARGETS).
            </div>
          </section>
        )}
        {refresh?.last_error && (
          <div className="panel p-4 text-sm text-brand font-mono break-words" data-testid="admin-last-error">{refresh.last_error}</div>
        )}

        <section className="grid grid-cols-1 lg:grid-cols-3 gap-3" data-testid="admin-computed-numbers">
          <div className="panel p-4">
            <div className="eyebrow mb-2">Venues</div>
            {all?.totals ? (
              <dl className="text-sm space-y-1">
                {[
                  ["Total venues (all time)", all.totals.total], ["· Legacy base", all.totals.legacy], ["· Won", all.totals.won],
                  ["Products sold (all time)", all.totals.products_sold], ["Pipeline (onboarding, not counted)", all.totals.onboarding],
                  ["Test deals excluded", dq?.excluded_tests?.length || 0], ["Default view counts from", status.count_from ? `${status.count_from} (1st of the month – restarts monthly)` : "—"],
                  ["New venues since that date", since?.totals?.total], ["· products sold", since?.totals?.products_sold], ["· of which upsells to existing venues", since?.totals?.upsell_products],
                ].map(([k, v]) => (
                  <div key={k} className="flex justify-between gap-3"><dt className="text-slate-500">{k}</dt><dd className="num font-bold">{typeof v === "number" ? fmt(v) : v}</dd></div>
                ))}
                <div className="flex justify-between gap-3 pt-1 border-t border-panel-border" data-testid="admin-without-venue-row">
                  <dt className="text-slate-500">Deals on no Venue company <span className="text-slate-400">(not counted · {fmt(dq?.without_venue?.filter((d) => d.recent).length || 0)} won since {trackingFrom})</span></dt>
                  <dd data-testid="admin-without-venue-count" className={`num font-bold ${dq?.without_venue?.length ? "text-brand" : ""}`}>{fmt(dq?.without_venue?.length || 0)}</dd>
                </div>
                <div className="flex justify-between gap-3" data-testid="admin-via-merchant-row">
                  <dt className="text-slate-500">Legacy deals counted via Merchant → Venue link</dt>
                  <dd data-testid="admin-via-merchant-count" className="num font-bold">{fmt(dq?.via_merchant?.length || 0)}</dd>
                </div>
              </dl>
            ) : <div className="text-sm text-slate-500">No snapshot yet</div>}
          </div>
          <div className="panel p-4">
            <div className="eyebrow mb-2">Products · venues with the product (all time)</div>
            <dl className="text-sm space-y-1">
              {PRODUCT_ORDER.map((p) => (
                <div key={p} className="flex justify-between gap-3" data-testid={`admin-product-${PRODUCT_SLUG(p)}`}><dt className="text-slate-500">{PRODUCT_LABEL[p]}{PRODUCT_LABEL[p] !== p ? <span className="text-slate-400"> · {p}</span> : null}</dt><dd className="num font-bold">{fmt(all?.products?.[p])} <span className="text-slate-400 font-normal">/ since {fmt(since?.products?.[p])}</span></dd></div>
              ))}
            </dl>
          </div>
          <div className="panel p-4">
            <div className="eyebrow mb-2">Countries · venues (all time)</div>
            <dl className="text-sm space-y-1 max-h-64 overflow-y-auto thin-scroll pr-1">
              {(all?.countries || []).map((c) => (
                <div key={c.code} className="flex justify-between gap-3"><dt className="text-slate-500">{c.code === "ZZ" ? "No country" : `${c.code} · ${countryName(c.code)}`}</dt><dd className="num font-bold">{fmt(c.total)}</dd></div>
              ))}
            </dl>
          </div>
        </section>

        <DisplayKeys />

        <HygieneAdmin hygiene={status?.hygiene} countFrom={status?.tracking_from || status?.count_from} />

        <AiDecisions />

        <section>
          <div className="eyebrow mb-3">Data quality</div>
          {status?.owners_available === false && (
            <div className="panel p-4 mb-3 text-sm text-ink border-brand/30 bg-brand/5" data-testid="admin-owners-scope-note">
              <b>Deal owner names unavailable.</b> The HubSpot private app token has no <code className="num text-xs bg-panel px-1 rounded">crm.objects.owners.read</code> scope, so the "Top owner" column and the per-owner split in country details are hidden.
              Fix: HubSpot → Settings → Integrations → Private apps → this app → Scopes → CRM → tick <i>crm.objects.owners.read</i> → Commit changes. The board picks it up on the next refresh (≤ 5 min) – the token itself stays the same.
            </div>
          )}
          <Tabs defaultValue="companies">
            <TabsList className="bg-slate-100 border border-panel-border flex-wrap h-auto">
              <TabsTrigger value="companies" data-testid="dq-tab-companies">Companies & links · {dq?.companies?.fetched ?? 0}</TabsTrigger>
              <TabsTrigger value="no-venue" data-testid="dq-tab-no-venue" className={dq?.without_venue?.some((d) => d.recent) ? "data-[state=inactive]:text-brand" : ""}>Deals not on a venue · {dq?.without_venue?.length ?? 0}</TabsTrigger>
              <TabsTrigger value="via-merchant" data-testid="dq-tab-via-merchant">Via merchant (legacy) · {dq?.via_merchant?.length ?? 0}</TabsTrigger>
              <TabsTrigger value="mapping" data-testid="dq-tab-mapping">Line items → products · {dq?.line_item_mapping?.length ?? 0}</TabsTrigger>
              <TabsTrigger value="no-product" data-testid="dq-tab-no-product">Won without line items · {dq?.won_without_product?.length ?? 0}</TabsTrigger>
              <TabsTrigger value="no-country" data-testid="dq-tab-no-country">Without country · {dq?.without_country?.length ?? 0}</TabsTrigger>
              <TabsTrigger value="tests" data-testid="dq-tab-tests">Excluded tests · {dq?.excluded_tests?.length ?? 0}</TabsTrigger>
              <TabsTrigger value="no-date" data-testid="dq-tab-no-date">Without stage date · {dq?.without_date?.length ?? 0}</TabsTrigger>
              <TabsTrigger value="partner-name" data-testid="dq-tab-partner-name" className={dq?.partner_missing_name?.length ? "data-[state=inactive]:text-amber-800" : ""}>Partner name missing · {dq?.partner_missing_name?.length ?? 0}</TabsTrigger>
            </TabsList>
            <TabsContent value="companies">
              <CompanyStats companies={dq?.companies} />
            </TabsContent>
            <TabsContent value="no-venue">
              <div className="text-xs text-slate-500 mb-2" data-testid="without-venue-rules">
                Won / Legacy deals with no company of <b>Company type = Venue</b> linked (directly or through their Merchant). <b>Not counted</b> on the board – associate the Venue in HubSpot (or set Company type = Venue on the right company) and it appears after the next refresh. Deals won since {trackingFrom} first.
              </div>
              <DealList items={dq?.without_venue} testId="data-quality-no-venue" render={(it) => (<><Id id={it.id} /><span className="flex-1 truncate">{it.url ? <a href={it.url} target="_blank" rel="noreferrer" className="hover:text-brand hover:underline" title="Open the deal in HubSpot">{it.name}</a> : it.name}{it.companies?.length ? <span className="text-slate-400"> · set Company type on {it.companies.map((c, i) => <span key={c.id}>{i ? ", " : ""}<a href={c.url} target="_blank" rel="noreferrer" className="text-ink hover:text-brand hover:underline" title="Open the company in HubSpot">{c.name}</a></span>)}</span> : it.company ? <span className="text-slate-400"> · on {it.company}</span> : null}</span>{it.recent ? <span className="num text-[10px] font-bold text-brand bg-brand/10 rounded-full px-2 py-0.5 whitespace-nowrap">won since {trackingFrom}</span> : null}{it.owner ? <span className="text-xs text-slate-600 whitespace-nowrap" title="Deal owner">{it.owner}</span> : null}<span className="text-xs text-slate-500 whitespace-nowrap">{it.reason}</span><span className="text-xs uppercase tracking-wider text-slate-500">{it.type}</span><Country code={it.country} /></>)} />
            </TabsContent>
            <TabsContent value="via-merchant">
              <div className="text-xs text-slate-500 mb-2" data-testid="via-merchant-rules">
                Legacy data: deals associated only to a <b>Merchant</b> company. They count for that Merchant's venues found through the company-to-company association (one deal with 3 venues = 3 clients). Associate the deal to the right Venue(s) to make it explicit.
              </div>
              <DealList items={dq?.via_merchant} testId="data-quality-via-merchant" render={(it) => (<><Id id={it.id} /><span className="flex-1 truncate" title={it.venues?.join(", ")}>{it.name}<span className="text-slate-400"> · {it.merchant} → {it.venues?.length === 1 ? it.venues[0] : `${it.venues?.length} venues: ${it.venues?.join(", ")}`}</span></span><span className="text-xs uppercase tracking-wider text-slate-500">{it.type}</span><Country code={it.country} /></>)} />
            </TabsContent>
            <TabsContent value="mapping">
              <div className="text-xs text-slate-500 mb-2">HubSpot line-item names and the product category they are counted under (×n = occurrences on Won deals). Rules live in backend/config.py PRODUCT_RULES.</div>
              <LineItemMapping items={dq?.line_item_mapping} />
            </TabsContent>
            <TabsContent value="no-product">
              <DealList items={dq?.won_without_product} testId="data-quality-no-product" render={(it) => (<><Id id={it.id} /><span className="flex-1 truncate">{it.name}</span><Country code={it.country} /></>)} />
            </TabsContent>
            <TabsContent value="no-country">
              <DealList items={dq?.without_country} testId="data-quality-no-country" render={(it) => (<><Id id={it.id} /><span className="flex-1 truncate">{it.name}</span><span className="text-xs uppercase tracking-wider text-slate-500">{it.type}</span></>)} />
            </TabsContent>
            <TabsContent value="tests">
              <DealList items={dq?.excluded_tests} testId="data-quality-tests" render={(it) => (<><Id id={it.id} /><span className="flex-1 truncate">{it.name}</span>{it.ai ? <span className="text-[10px] font-bold uppercase tracking-wider text-brand" title="Recognised by AI (Jev) – restore in AI decisions if it is a real client">AI</span> : null}{it.type ? <span className="text-xs uppercase tracking-wider text-slate-500">{it.type}</span> : null}</>)} />
            </TabsContent>
            <TabsContent value="no-date">
              <DealList items={dq?.without_date} testId="data-quality-no-date" render={(it) => (<><Id id={it.id} /><span className="flex-1 truncate">{it.name}</span><span className="text-xs uppercase tracking-wider text-slate-500">{it.type}</span></>)} />
            </TabsContent>
            <TabsContent value="partner-name">
              <div className="text-xs text-slate-500 mb-2" data-testid="partner-name-rules">
                Venues won through the <b>Partnership</b> / <b>Channel partner</b> pipelines whose deal has an empty <b>“Partner Name”</b> property in HubSpot – one row per deal (a deal can cover several venues). Fill it in on the deal (Deal → Partner Name) and the board shows a per-partner split in the “Via partners” strip after the next refresh. Newest first.
              </div>
              <DealList
                items={dq?.partner_missing_name}
                testId="data-quality-partner-name"
                render={(it) => (
                  <div className="flex-1 min-w-0 grid grid-cols-[minmax(0,1fr)_auto] sm:flex sm:items-center gap-x-3 gap-y-1">
                    <span className="flex-1 min-w-0 truncate col-span-2 sm:col-span-1 order-first" title={it.venues?.join(", ")}>
                      <span className="num text-xs text-slate-500 mr-2">{it.id}</span>
                      <span className="font-medium text-ink">{it.name}</span>
                      {it.venues?.length ? <span className="text-slate-400"> · {it.count === 1 ? it.venues[0] : `${it.count} venues: ${it.venues.join(", ")}`}</span> : null}
                    </span>
                    <span className="text-xs text-slate-500 truncate sm:max-w-[150px]" title={`Deal owner: ${it.owner}`}>{it.owner}</span>
                    <span className="flex items-center gap-2 justify-self-end">
                      <span className="num text-[10px] font-bold text-amber-700 bg-amber-100 rounded-full px-2 py-0.5 whitespace-nowrap">{it.pipeline}</span>
                      <span className="num text-xs text-slate-500 whitespace-nowrap">{it.won_day ? fmtDate(it.won_day) : "—"}</span>
                      <Country code={it.country} />
                    </span>
                  </div>
                )}
              />
            </TabsContent>
          </Tabs>
        </section>
      </div>
    </div>
  );
}
